/* eslint-disable react-refresh/only-export-components */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import type { StepperStep } from '@/components/shared/Stepper';
import { Stepper } from '@/components/shared/Stepper';
import { Button } from '@/components/ui/button';
import { useTranslator } from '@/hooks/useTranslator';
import { AddProductionStep1 } from '../-components/AddProductionStep1';
import { AddProductionStep2 } from '../-components/AddProductionStep2';
import { useAddProduction } from '../-hooks/useAddProduction';

export const Route = createFileRoute('/(client)/productions/create/')({
  component: CreateProductionPage,
});

function CreateProductionPage() {
  const { getTranslation: t } = useTranslator({ page: 'productions' });
  const navigate = useNavigate();

  const ctrl = useAddProduction(id => {
    void navigate({
      to: '/productions/$productionId',
      params: { productionId: id },
    });
  });

  const {
    form,
    isCurrentStepValid,
    handleNext,
    handleBack,
    handleSubmit,
    isSubmitting,
  } = ctrl;

  const steps: StepperStep[] = [
    { key: 'step1', label: t('create.step1Label') },
    { key: 'step2', label: t('create.step2Label') },
    { key: 'step3', label: t('create.step3Label') },
  ];

  return (
    <div className='flex flex-col flex-1 min-h-0 bg-card'>
      {/* Sticky stepper header */}
      <div className='px-6 py-3 border-b border-border shrink-0 flex justify-center'>
        <Stepper
          steps={steps}
          currentStep={form.currentStep + 1}
          direction='horizontal'
          className='max-w-2xl w-full'
        >
          {null}
        </Stepper>
      </div>

      {/* Step content */}
      <div className='flex-1 overflow-y-auto px-6 py-6 max-w-3xl w-full mx-auto'>
        {form.currentStep === 0 && <AddProductionStep1 ctrl={ctrl} />}
        {form.currentStep === 1 && <AddProductionStep2 ctrl={ctrl} />}
        {form.currentStep === 2 && (
          <div className='flex flex-col gap-4'>
            <div>
              <p className='text-sm font-semibold'>{t('create.step3Label')}</p>
              <p className='text-xs text-muted-foreground mt-0.5'>
                {t('create.step3.requiredDocs')}
              </p>
            </div>
            {/* TODO: DocumentRulesTable with production template */}
          </div>
        )}
      </div>

      {/* Sticky footer */}
      <div className='px-6 py-4 border-t border-border shrink-0 flex items-center justify-end gap-3'>
        <Button
          variant='outline'
          onClick={() => void navigate({ to: '/productions' })}
        >
          {t('create.close')}
        </Button>
        {form.currentStep > 0 && (
          <Button variant='outline' onClick={handleBack}>
            Back
          </Button>
        )}
        <Button
          disabled={!isCurrentStepValid || isSubmitting}
          onClick={form.currentStep < 2 ? handleNext : handleSubmit}
        >
          {isSubmitting && <Loader2 className='size-3.5 animate-spin mr-1.5' />}
          {form.currentStep < 2 ? t('create.next') : t('create.submit')}
        </Button>
      </div>
    </div>
  );
}
